import _ from 'lodash';
import { memo, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { PipeDefinitionUtility } from 'v2/common/quantConfig/index';
import { Controller } from 'react-hook-form';
import useOutputSchema from 'v2/modules/withRunForm/hooks/useOutputSchema';
import { useSelector } from 'react-redux';
import { getSelectorBasedStoreValue } from 'v2/modules/withRunForm/redux/selector';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import { OutputSchemaUtility, TypeheadSchemaUtility } from 'v2/common/quantConfig/outputSchema/utility';
import { schemaShouldShowConfigTypes } from 'v2/common/quantConfig/outputSchema/config';
import CustomTypeAhead from 'v3/common/components/CustomTypeAhead';

const propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  type: PropTypes.string.isRequired,
  onUpdatePipeConfig: PropTypes.func.isRequired,
  validator: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  pipeIndex: PropTypes.number,
  pipeConfig: PropTypes.shape({}),
};

const defaultProps = {
  pipeIndex: undefined,
  pipeConfig: {},
};

const mandatoryMultipleInstrumentForPipes = () => [PipeDefinitionUtility.getPipeConfigDefaults('ratio').type];

const isInvalidForInstrument = (type, valuePaths, maxInstrumentsInGroup) => {
  const hasDefaultValuePaths =
    _.filter(
      _.get(PipeDefinitionUtility.getPipeConfigDefaults(type), 'config.valuePaths', []),
      (valuePath) => {
        return _.includes(valuePaths, valuePath);
      }
    ).length === valuePaths.length;

  return (
    _.includes(mandatoryMultipleInstrumentForPipes(), type) &&
    maxInstrumentsInGroup === 1 &&
    hasDefaultValuePaths
  );
};

const ValuePathsFormField = ({
  value,
  type,
  onUpdatePipeConfig,
  validator,
  control,
  errors,
  name: propsName,
  pipeIndex,
  pipeConfig,
}) => {
  const uniqueId = useMemo(() => _.uniqueId('pipe_value_paths_'), []);
  const errorMessage = _.get(errors, `${uniqueId}.message`, '');
  const maxInstrumentsInGroup = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'quantConfig.maxInstrumentsInGroup')
  );

  const hasCombinedPremium = (val) => {
    if (_.isString(val)) return val.includes('combinedPremium');
    if (_.isPlainObject(val)) return _.get(val, 'id', '').includes('combinedPremium');
    return false;
  };

  const isDayCandleIntervalWithPremium = (itemValue) =>
    _.get(pipeConfig, 'config.candleInterval', '') === '1 days' &&
    _.some(_.castArray(itemValue), hasCombinedPremium);

  const outputSchema = useOutputSchema();
  const isInvalid = useMemo(
    () => isInvalidForInstrument(type, value, maxInstrumentsInGroup),
    [maxInstrumentsInGroup, type, value]
  );

  const pipeConfigOutputSchema = useMemo(
    () =>
      _.chain(outputSchema)
        .thru((schema) =>
          OutputSchemaUtility.filterByTypes(schema, [schemaShouldShowConfigTypes.pipeValuePath])
        )
        .thru((schema) => OutputSchemaUtility.filterSchemaPriorToPipeIndex(schema, pipeIndex))
        .value(),
    [outputSchema, pipeIndex]
  );

  const isNonEditableValuePaths = useMemo(
    () => _.includes(PipeDefinitionUtility.getIndicatorsWithNonEditableValuePaths(), type),
    [type]
  );

  const options = useMemo(
    () => _.map(pipeConfigOutputSchema, (schema) => TypeheadSchemaUtility.getOption(schema)),
    [pipeConfigOutputSchema]
  );

  const selected = useMemo(
    () => TypeheadSchemaUtility.getSelectedOption(pipeConfigOutputSchema, value),
    [value, pipeConfigOutputSchema]
  );

  const onValuePathChange = useCallback(
    (val, onChange) => {
      const newValuePaths = _.map(val, 'id');

      onUpdatePipeConfig(propsName, newValuePaths);
      onChange(newValuePaths);
    },
    [onUpdatePipeConfig, propsName]
  );

  const valuePathValidator = {
    validate: (val) => {
      if (_.isEmpty(val)) return 'Please add a value Field';
      if (isInvalid) return 'should have pair trade';
      if (isDayCandleIntervalWithPremium(val)) return 'Combined Premium Cannot Support Day Candle';
      return null;
    },
  };

  const typeaheadRef = useRef(null);

  return (
    <ErrorMesssage message={errorMessage}>
      <Controller
        render={({ onChange, name }) => (
          <CustomTypeAhead
            className="custom-form-control"
            multiple={_.get(validator, 'minLength.value', 1) > 1}
            disabled={isNonEditableValuePaths}
            ref={typeaheadRef}
            isInvalid={!!errorMessage}
            name={name}
            options={options}
            selectedItems={selected}
            labelKey="searchLabel"
            filterBy={(option, filterProps) =>
              TypeheadSchemaUtility.getOptionFilteredBySearchText(option, filterProps)
            }
            clearButton={!_.isEmpty(selected)}
            onChange={(val) => {
              if (val.length === 0) {
                typeaheadRef.current?.focus();
                typeaheadRef.current?.toggleMenu();
              }
              onValuePathChange(val, onChange);
            }}
          />
        )}
        control={control}
        defaultValue={selected}
        name={uniqueId}
        rules={valuePathValidator}
      />
    </ErrorMesssage>
  );
};

ValuePathsFormField.propTypes = propTypes;
ValuePathsFormField.defaultProps = defaultProps;

export default memo(ValuePathsFormField);
